/**
 * Admin Agents RPC definitions (CLA-426)
 *
 * The unified agents RpcGroup behind the collapsed admin Agents page.
 * `create` provisions a new agent row (Mac or Cloud) and bills the prorated
 * seat on the customer's Stripe subscription (or skips per the `billing`
 * action / reseller attribution), returning the payment outcome; `mintInstallLink`
 * mints a one-shot Mac install token (or starts cloud provisioning and
 * returns a progress handle); `lifecycle` drives stop/start/deprovision with
 * legal-transition enforcement; `fleetList` returns ONE merged row per agent
 * (account fields + node health) and supersedes the separate installs/nodes
 * lists. All endpoints require admin role.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import {
  AgentInstallationStatus,
  BrowserKind,
  CloudProvider,
  DeploymentType,
  SeatBillingMode,
} from "@fm/db/models/AgentInstallation";
import { AgentRatePlan } from "@fm/db/models/Customer";
import { SubscriptionPlan, SubscriptionStatus } from "@fm/db/models/Subscription";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import {
  DatabaseError,
  Forbidden,
  InvalidStateTransition,
  RecordNotFoundError,
  SeatBillingError,
  SubscriptionError,
  Unauthorized,
  ValidationError,
  ValkeyError,
} from "../errors.ts";
import { InstallTokenCipherError, WorkflowDispatchFailedError } from "./installs.rpc.ts";
import { NodeHealth } from "./nodes.rpc.ts";

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Create-agent input. `region` is only meaningful for `cloud-vps` agents —
 * the handler rejects a cloud create without one via a typed
 * {@link ValidationError}; `local-mac` agents need no region. `name` is
 * optional — the handler generates a slug-safe name when omitted. `intakeId`
 * links the intake-seeded first agent (CLA-402); omit for self-serve agents.
 *
 * `cloudProvider` opts a `cloud-vps` agent into a specific cloud. Omitted =>
 * the intake-seeded provider when `intakeId` resolves, else `"hetzner"` (the
 * DigitalOcean default flip is a later ticket). A `digitalocean` create may
 * pass EITHER a Hetzner region slug (`ash`/`fsn1`, translated at the workflow
 * seam) or a native DO slug (`nyc3`) in `region`.
 *
 * `billing` chooses how the CSR add is charged. Default `"charge"` bills the
 * prorated seat on the customer's ONE Stripe subscription off-session (via
 * {@link SeatBillingService}); `"skip"` comps the seat (no Stripe mutation) and
 * logs the acting admin; `"invoice"` charges NOTHING now — it creates + finalizes
 * a hosted Stripe invoice (the approval link a CSR sends the customer) and gates
 * both the seat quantity change and cloud provisioning on the platform
 * `invoice.paid` webhook. A reseller-attributed customer ALWAYS skips platform
 * billing regardless of this flag — their billing is the connected-account
 * subscription. Unlike the self-serve procure flow (which defers the agent row
 * until payment settles), the admin add-agent path ALWAYS inserts the row: a
 * CSR add is an explicit operator decision, so an SCA `requires_action` outcome
 * is RETURNED for the admin/customer to settle rather than blocking the create.
 *
 * `installFeeCents` is honored ONLY in `invoice` mode: a one-time install-fee
 * line riding the same hosted invoice. Omitted = no install-fee line (waived).
 *
 * @since 1.0.0
 */
export const AgentsCreateInput = Schema.Struct({
  customerId: Schema.NonEmptyTrimmedString,
  deploymentType: DeploymentType,
  /** Which cloud for cloud-vps agents. Omitted = "digitalocean" (the standing default). */
  cloudProvider: Schema.optional(CloudProvider),
  region: Schema.optional(Schema.String),
  name: Schema.optional(Schema.NonEmptyTrimmedString),
  /** Which browser to provision (chrome | camoufox). Omitted = "chrome". */
  browser: Schema.optional(BrowserKind),
  intakeId: Schema.optional(Schema.String),
  /** How to bill the CSR add. Omitted = "charge" (prorated saved-PM seat charge). */
  billing: Schema.optional(Schema.Literal("charge", "skip", "invoice")),
  /**
   * One-time install-fee (cents) to ride the hosted invoice in `invoice` mode.
   * Omitted = waived (single seat line only). Ignored outside `invoice` mode.
   */
  installFeeCents: Schema.optional(Schema.Int),
});
export type AgentsCreateInput = Schema.Schema.Type<typeof AgentsCreateInput>;

/**
 * Single-agent input for `mintInstallLink`.
 *
 * @since 1.0.0
 */
export const AgentByIdInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
});
export type AgentByIdInput = Schema.Schema.Type<typeof AgentByIdInput>;

/**
 * Lifecycle actions the admin Agents page can drive. Each maps to a target
 * {@link AgentInstallationStatus} (`stop → stopped`, `start → active`,
 * `deprovision → deprovisioned`) gated by the legal-transitions table.
 *
 * @since 1.0.0
 */
export const AgentLifecycleAction = Schema.Literal("stop", "start", "deprovision");
export type AgentLifecycleAction = Schema.Schema.Type<typeof AgentLifecycleAction>;

/**
 * Lifecycle input — the agent id plus the requested action.
 *
 * @since 1.0.0
 */
export const AgentsLifecycleInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
  action: AgentLifecycleAction,
});
export type AgentsLifecycleInput = Schema.Schema.Type<typeof AgentsLifecycleInput>;

/**
 * Fleet-list filters. `status` filters on the account-side lifecycle status,
 * `health` on the Valkey-derived node health, `customerId` and `region` on
 * the account fields. All optional — no filters returns the whole fleet.
 *
 * @since 1.0.0
 */
export const AgentsFleetListInput = Schema.Struct({
  status: Schema.optional(AgentInstallationStatus),
  health: Schema.optional(NodeHealth),
  customerId: Schema.optional(Schema.String),
  region: Schema.optional(Schema.String),
});
export type AgentsFleetListInput = Schema.Schema.Type<typeof AgentsFleetListInput>;

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Compact agent projection returned by `create`.
 *
 * @since 1.0.0
 */
export const AgentSummary = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  customerId: Schema.NullOr(Schema.String),
  intakeId: Schema.NullOr(Schema.String),
  deploymentType: Schema.NullOr(DeploymentType),
  /** How the platform seat is billed (CLA-647); null on legacy rows. */
  seatBilling: Schema.NullOr(SeatBillingMode),
  region: Schema.String,
  size: Schema.String,
  status: AgentInstallationStatus,
  createdAt: Schema.DateTimeUtc,
});
export type AgentSummary = Schema.Schema.Type<typeof AgentSummary>;

/**
 * The customer's subscription row as read back after `create` — sourced from
 * the (Stripe-webhook-updated) `subscriptions` row, NOT mutated by `create`
 * itself. Surfaced so the admin UI can confirm the standing subscription
 * without a follow-up RPC. `null` when the customer has no local subscription
 * row (e.g. a reseller-attributed customer billed on the connected account).
 *
 * @since 1.0.0
 */
export const AgentSubscriptionSummary = Schema.Struct({
  id: Schema.String,
  customerId: Schema.String,
  installationId: Schema.NullOr(Schema.String),
  plan: SubscriptionPlan,
  status: SubscriptionStatus,
  amount: Schema.NullOr(Schema.Number),
});
export type AgentSubscriptionSummary = Schema.Schema.Type<typeof AgentSubscriptionSummary>;

/**
 * The billing outcome of an admin add-agent, discriminated by `status`:
 *
 *  - `paid` — the prorated seat charge settled immediately off-session against
 *    the saved payment method; `proratedChargeCents` is the amount collected.
 *  - `requires_action` — the off-session charge needs Strong Customer
 *    Authentication; `clientSecret` confirms it on-session and `invoiceId` is
 *    the invoice awaiting confirmation. The agent row already EXISTS (the admin
 *    add is an operator decision) — the admin UI or the customer settles this.
 *  - `skipped` — no Stripe mutation happened. `reason` distinguishes an explicit
 *    admin comp (`admin_skip`), a reseller-attributed customer whose billing is
 *    on the connected account (`reseller_attributed`), or the intake-seeded
 *    first agent already paid for by the onboarding checkout (`intake_checkout`).
 *  - `invoice_sent` — `billing: "invoice"`. A hosted Stripe invoice (the customer
 *    approval link) was created + finalized carrying the prorated seat line plus
 *    an optional install-fee line; `hostedInvoiceUrl` is the shareable payment
 *    link (null when Stripe emits none), `invoiceId` the finalized invoice, and
 *    `totalCents` the invoiced amount. NO seat quantity change and NO provisioning
 *    happen until the platform `invoice.paid` webhook fires.
 *
 * @since 1.0.0
 */
export const AgentsCreatePayment = Schema.Union(
  Schema.Struct({ status: Schema.Literal("paid"), proratedChargeCents: Schema.Int }),
  Schema.Struct({ status: Schema.Literal("requires_action"), clientSecret: Schema.String, invoiceId: Schema.String }),
  Schema.Struct({
    status: Schema.Literal("skipped"),
    reason: Schema.Literal("admin_skip", "reseller_attributed", "intake_checkout"),
  }),
  Schema.Struct({
    status: Schema.Literal("invoice_sent"),
    hostedInvoiceUrl: Schema.NullOr(Schema.String),
    invoiceId: Schema.String,
    totalCents: Schema.Int,
  }),
);
export type AgentsCreatePayment = Schema.Schema.Type<typeof AgentsCreatePayment>;

/**
 * `create` result — the new agent, the customer's standing subscription (read
 * back, `null` when none), and the `payment` outcome of the seat billing action
 * (added in CLA-606; the `agent` + `subscription` shape is preserved so the
 * existing admin web modal keeps working until it consumes `payment`).
 *
 * @since 1.0.0
 */
export const AgentsCreateResult = Schema.Struct({
  agent: AgentSummary,
  subscription: Schema.NullOr(AgentSubscriptionSummary),
  payment: AgentsCreatePayment,
});
export type AgentsCreateResult = Schema.Schema.Type<typeof AgentsCreateResult>;

/**
 * `mintInstallLink` result, discriminated by `deploymentType`:
 *
 *  - `local-mac` — a one-shot hashed install token was minted;
 *    `installToken`, `installUrl` (the customer-facing curl one-liner) and
 *    `installTokenExpiresAt` are populated, `progressHandle` is null.
 *  - `cloud-vps` — provisioning was dispatched; `progressHandle` carries the
 *    installation id to feed `admin/installs/streamActivity` for live
 *    progress, the Mac fields are null.
 *
 * @since 1.0.0
 */
export const AgentsMintInstallLinkResult = Schema.Struct({
  agentId: Schema.String,
  deploymentType: DeploymentType,
  installToken: Schema.NullOr(Schema.String),
  installUrl: Schema.NullOr(Schema.String),
  installTokenExpiresAt: Schema.NullOr(Schema.DateTimeUtc),
  progressHandle: Schema.NullOr(Schema.String),
  /**
   * Which hand-off the caller should render:
   *  - `install` — a fresh box; `installUrl` is the install one-liner.
   *  - `adopt` — an EXISTING box the customer already runs; the self-serve
   *    adopt command joins it to the fleet.
   *  - `cloud` — provisioning was dispatched; follow `progressHandle`.
   */
  installKind: Schema.Literal("install", "adopt", "cloud"),
});
export type AgentsMintInstallLinkResult = Schema.Schema.Type<typeof AgentsMintInstallLinkResult>;

/**
 * `lifecycle` result — the agent's post-transition status plus the
 * deprovision stamp when the action was `deprovision`.
 *
 * @since 1.0.0
 */
export const AgentsLifecycleResult = Schema.Struct({
  id: Schema.String,
  status: AgentInstallationStatus,
  deprovisionedAt: Schema.NullOr(Schema.DateTimeUtc),
});
export type AgentsLifecycleResult = Schema.Schema.Type<typeof AgentsLifecycleResult>;

/**
 * `reprovision` result — re-dispatches the VPS provisioning workflow
 * for a failed/stuck cloud agent WITHOUT deleting DB rows. A fresh
 * per-attempt token makes the workflow execute anew rather than
 * returning a cached terminal failure. `progressHandle` carries the
 * installation id to feed `admin/installs/streamActivity` for live
 * progress.
 *
 * @since 1.0.0
 */
export const AgentsReprovisionResult = Schema.Struct({
  agentId: Schema.String,
  deploymentType: DeploymentType,
  progressHandle: Schema.String,
});
export type AgentsReprovisionResult = Schema.Schema.Type<typeof AgentsReprovisionResult>;

/**
 * ONE merged fleet row per agent — account fields (name, customer, region,
 * size, lifecycle status, the customer's standing per-agent rate) plus node
 * health derived from the Valkey heartbeat. Supersedes the separate
 * installs/nodes list shapes for the collapsed Agents page.
 *
 * `rateAmount` is the customer's `agentRateAmount` in cents and `ratePlan`
 * its cadence — both null when the agent has no customer or the customer has
 * no negotiated rate.
 *
 * @since 1.0.0
 */
export const FleetAgentRow = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  customerId: Schema.NullOr(Schema.String),
  customerName: Schema.NullOr(Schema.String),
  intakeId: Schema.NullOr(Schema.String),
  deploymentType: Schema.NullOr(DeploymentType),
  /** How the platform seat is billed (CLA-647); null on legacy rows. Drives the "comped" badge. */
  seatBilling: Schema.NullOr(SeatBillingMode),
  region: Schema.String,
  size: Schema.String,
  status: AgentInstallationStatus,
  health: NodeHealth,
  rateAmount: Schema.NullOr(Schema.Int),
  ratePlan: Schema.NullOr(AgentRatePlan),
  lastSeenAt: Schema.NullOr(Schema.DateTimeUtc),
  agentVersion: Schema.NullOr(Schema.String),
  createdAt: Schema.DateTimeUtc,
});
export type FleetAgentRow = Schema.Schema.Type<typeof FleetAgentRow>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Agents RPC group.
 *
 * All endpoints require admin role via `AdminMiddleware`. Tags are
 * namespaced under `admin/agents/`.
 *
 * @since 1.0.0
 */
export class AdminAgentsRpcs extends RpcGroup.make(
  Rpc.make("create", {
    payload: AgentsCreateInput,
    success: AgentsCreateResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      ValidationError,
      RecordNotFoundError,
      DatabaseError,
      SeatBillingError,
      SubscriptionError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("mintInstallLink", {
    payload: AgentByIdInput,
    success: AgentsMintInstallLinkResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      InstallTokenCipherError,
      WorkflowDispatchFailedError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("lifecycle", {
    payload: AgentsLifecycleInput,
    success: AgentsLifecycleResult,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, InvalidStateTransition, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("reprovision", {
    payload: AgentByIdInput,
    success: AgentsReprovisionResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      ValidationError,
      DatabaseError,
      WorkflowDispatchFailedError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("fleetList", {
    payload: AgentsFleetListInput,
    success: Schema.Array(FleetAgentRow),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, ValkeyError),
  }).middleware(AdminMiddleware),
).prefix("admin/agents/") {}
